import { useCallback, useEffect, useState } from "react";
import { MessageCircle, CalendarCheck, Bell, Plus, ChevronRight, QrCode, Hash, KeyRound, Unplug, Trash2, RefreshCw, AlertTriangle, Check, Clock } from "lucide-react";
import PasswordInput from "../../../components/PasswordInput";
import { CountryPhoneSelector } from "../../../components/CountryPhoneSelector";
import { validatePhoneForCountry, normalizePhoneToInternational } from "../../../services/countries/phoneNormalizer";
import { confirmDialog, showError, showSuccess, showWarning, notifyApiError } from "../../../components/alertas";
import CalendarService from "../../../services/calendar/CalendarService";
import ClientService from "../../../services/client/ClientService";
import useWhatsappSocket from "../../../hooks/useWhatsappSocket";
import { mostrarSpinner, ocultarSpinner } from "../../../components/Spinner";
import { setupPushNotifications } from "../../../hooks/usePushNotifications";
import Sheet from "../../../components/sheet/Sheet";
import useMediaQuery from "../../../hooks/useMediaQuery";
import logger from "../../../utils/logger";
import "./conexiones.css";

const URI_GCALENDAR = process.env.REACT_APP_URI_GCALENDAR || 'http://localhost:4000/api/google/calendar/auth';
const initials = (name = "") => name.trim().split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() || "").join("");
const currentPushPermission = () => (typeof Notification !== "undefined" ? Notification.permission : "default");

/**
 * Conexiones: WhatsApp y Google Calendar como tarjetas de estado con una acción
 * clara; profesionales como lista (cada uno abre su sheet); QR / código de
 * vinculación y alta de profesional en sheets. Un staff solo ve su propia fila.
 */
function ConnectionsTab({ clientData, checkLimits, onConfigComplete, refetchClientData, userData }) {
    const isDesktop = useMediaQuery("(min-width: 992px)");
    const side = isDesktop ? "right" : "bottom";

    const [readyCount, setReadyCount] = useState(0);
    const [showAddStaff, setShowAddStaff] = useState(false);
    const [staffForEdit, setStaffForEdit] = useState(null);
    const [staffList, setStaffList] = useState(clientData?.staff || []);
    const [isAddingStaff, setIsAddingStaff] = useState(false);
    const [isWhatsAppConnected, setIsWhatsAppConnected] = useState(false);
    const [showWhatsAppSheet, setShowWhatsAppSheet] = useState(false);
    const [qrCode, setQrCode] = useState(null);
    const [isLoadingQr, setIsLoadingQr] = useState(false);
    const [connectMode, setConnectMode] = useState('qr'); // 'qr' | 'code'
    const [phoneInput, setPhoneInput] = useState('');
    const [pairingCode, setPairingCode] = useState(null);
    const [phoneCountry, setPhoneCountry] = useState('MX');
    const [phoneError, setPhoneError] = useState('');
    const [isLoadingCode, setIsLoadingCode] = useState(false);
    const [pushPermission, setPushPermission] = useState(currentPushPermission);
    const [pushBusy, setPushBusy] = useState(false);

    const isCurrentUserStaff = userData?.userType === 'staff';
    const currentStaffInfo = isCurrentUserStaff ? staffList.find(s => s.name === userData?.username) : null;
    const displayedStaffList = isCurrentUserStaff && currentStaffInfo ? [currentStaffInfo] : staffList;

    useEffect(() => {
        if (!clientData) return;
        setIsWhatsAppConnected(clientData?.whatsintegration?.status === 'authenticated' || clientData?.whatsintegration?.status === 'ready');
    }, [clientData]);

    useEffect(() => { setStaffList(clientData?.staff || []); }, [clientData]);

    useEffect(() => {
        const currentReady = staffList.filter(s => s.status === 'ready').length;
        if (currentReady > readyCount) onConfigComplete(); // aumentó el número de calendarios conectados
        setReadyCount(currentReady);
    }, [staffList, readyCount, onConfigComplete]);

    const handleSuccessfulConnection = useCallback(() => {
        setQrCode(null); setIsLoadingQr(false); setPairingCode(null); setIsLoadingCode(false); setPhoneInput('');
        setIsWhatsAppConnected(true);
        setShowWhatsAppSheet(false);
        showSuccess("¡Conectado!", "Tu cuenta de WhatsApp se ha conectado exitosamente.");
    }, []);
    const handleQr = useCallback((qr) => { setQrCode(qr); setIsLoadingQr(false); }, []);
    const handlePairingCode = useCallback((code) => { setPairingCode(code); setIsLoadingCode(false); }, []);
    const handleError = useCallback((error) => {
        setIsLoadingQr(false); setIsLoadingCode(false);
        showError("Error", error);
        setShowWhatsAppSheet(false);
    }, []);
    useWhatsappSocket(clientData?.id, handleQr, handleSuccessfulConnection, handleError, handlePairingCode);

    const startQr = async () => {
        setQrCode(null); setPairingCode(null); setIsLoadingCode(false);
        setIsLoadingQr(true);
        try {
            await ClientService.getWhatsAppQr(clientData.id); // solo inicia el proceso; el QR llega por socket
        } catch (error) {
            logger.error("Error iniciando QR:", error);
            setIsLoadingQr(false);
            showError("Error", "No se pudo iniciar la conexión. Intenta nuevamente.");
            setShowWhatsAppSheet(false);
        }
    };

    const handleWhatsAppConnection = async () => {
        if (isWhatsAppConnected) {
            const ok = await confirmDialog({
                title: "¿Desconectar WhatsApp?",
                text: "El bot dejará de contestar y de mandar recordatorios hasta que lo vuelvas a vincular.",
                confirmButtonText: "Sí, desconectar",
                cancelButtonText: "No",
                icon: "warning",
                danger: true,
            });
            if (!ok) return;
            mostrarSpinner();
            try {
                await CalendarService.disconnectWhatsApp(clientData.id);
                setIsWhatsAppConnected(false);
                showSuccess("Desconectado", "Tu cuenta de WhatsApp ha sido desconectada correctamente.");
            } catch (error) {
                logger.error("Error al desconectar:", error);
                showError("Error", "No se pudo desconectar WhatsApp. Intenta nuevamente.");
            } finally {
                ocultarSpinner();
            }
            return;
        }
        setConnectMode('qr');
        setPhoneInput('');
        setShowWhatsAppSheet(true);
        startQr();
    };

    const switchConnectMode = (mode) => {
        if (mode === connectMode) return;
        setConnectMode(mode);
        if (mode === 'qr') startQr();
        else { setIsLoadingQr(false); setPairingCode(null); } // el código se pide con el número
    };

    const handleRequestPairingCode = async () => {
        const check = validatePhoneForCountry(phoneInput || '', phoneCountry);
        if (!check.valid) {
            showError("Número inválido", check.message);
            return;
        }
        setPairingCode(null);
        setIsLoadingCode(true);
        try {
            await ClientService.getWhatsAppPairingCode(clientData.id, normalizePhoneToInternational(phoneInput, phoneCountry));
        } catch (error) {
            logger.error("Error solicitando código:", error);
            setIsLoadingCode(false);
            showError("Error", "No se pudo generar el código. Intenta nuevamente.");
        }
    };

    const handleAddStaff = async (staffName, accountData) => {
        const limitCheck = checkLimits('staff');
        if (!limitCheck.allowed) { showWarning('Límite de tu plan', limitCheck.message); return false; }
        if (accountData) {
            if (!accountData.username.trim() || !accountData.password) { showError("Campos requeridos", "Completa usuario y contraseña"); return false; }
            if (accountData.password !== accountData.confirmPassword) { showError("Error", "Las contraseñas no coinciden"); return false; }
        }
        setIsAddingStaff(true);
        try {
            const res = await ClientService.addStaff(clientData.id, staffName, accountData ? { username: accountData.username, password: accountData.password } : null);
            setStaffList([...staffList, { id: res.staffId, name: res.name, status: res.status }]);
            await refetchClientData();
            showSuccess("Éxito", accountData ? "Profesional agregado y cuenta creada exitosamente" : "Profesional agregado correctamente");
            return true;
        } catch (e) {
            const data = e?.response?.data;
            const msg = data?.message || "";
            // Límite de asientos = aviso (no error). Por código y, de respaldo, por el texto.
            if (data?.code === 'SEAT_LIMIT_REACHED' || /límite de tu plan|asiento/i.test(msg)) {
                showWarning("Límite de tu plan", msg || "Alcanzaste el límite de asientos de tu plan.");
            } else {
                notifyApiError(e, "Agregar profesional");
            }
            return false;
        } finally {
            setIsAddingStaff(false);
        }
    };

    const handleDisconnectGoogleCalendar = async (staffId) => {
        const ok = await confirmDialog({ title: "¿Desconectar Google Calendar?", text: "Este profesional dejará de recibir citas hasta que vuelva a conectar su calendario.", confirmButtonText: "Sí, desconectar", cancelButtonText: "No", icon: "warning", danger: true });
        if (!ok) return;
        try {
            await CalendarService.disconnectGoogleCalendar(clientData.id, staffId);
            setStaffList(list => list.map(staff => staff.id === staffId ? { ...staff, status: "pending" } : staff));
            showSuccess("Desconectado", "El profesional ha sido desconectado de Google Calendar.");
            setStaffForEdit(null);
        } catch (error) {
            showError("Error", "No se pudo desconectar Google Calendar. Intenta nuevamente.");
        }
    };

    const handleDeleteStaff = async (staffId) => {
        const ok = await confirmDialog({ title: "¿Eliminar este profesional?", text: "Esta acción no se puede deshacer.", confirmButtonText: "Sí, eliminar", cancelButtonText: "Cancelar", icon: "warning", danger: true });
        if (!ok) return;
        try {
            await ClientService.deleteStaff(clientData.id, staffId);
            setStaffList(list => list.filter(staff => staff.id !== staffId));
            await refetchClientData();
            showSuccess("Eliminado", "El profesional ha sido eliminado.");
            setStaffForEdit(null);
        } catch (error) {
            showError("Error", "No se pudo eliminar el profesional. Intenta nuevamente.");
        }
    };

    const connectGoogle = (staffId) => window.open(`${URI_GCALENDAR}?clientId=${clientData.id}&staffId=${staffId}`, "_blank");

    const handlePush = async () => {
        setPushBusy(true);
        try {
            const perm = await setupPushNotifications({ isStaff: isCurrentUserStaff });
            setPushPermission(perm);
            if (perm === 'granted') showSuccess("¡Activadas!", "Ahora recibirás notificaciones push.");
            else if (perm === 'denied') showError("Denegadas", "Las notificaciones push han sido denegadas. Puedes cambiarlo en la configuración del navegador.");
            else showError("Sin permiso", "No se concedió el permiso. Inténtalo de nuevo.");
        } finally {
            setPushBusy(false);
        }
    };

    if (!clientData) {
        return <div className="cp-stack">{[0, 1].map(i => <div key={i} className="cp-skeleton" style={{ height: 120 }} />)}</div>;
    }

    const connectedNumber = clientData?.whatsintegration?.connectedNumber;
    const readyStaff = staffList.filter(s => s.status === 'ready').length;

    return (
        <section className="cp-conn">
            <header className="cp-page-head">
                <div>
                    <h2 className="cp-page-title">Conexiones</h2>
                    <p className="cp-page-sub">Por dónde entran las citas y dónde se guardan.</p>
                </div>
            </header>

            <div className="cp-conn-grid">
                <div className="cp-conn-col">
                    {!isCurrentUserStaff && (
                        <article className="cp-card cp-conn-card" data-tour="conexiones.whatsapp">
                            <div className={`cp-conn-icon ${isWhatsAppConnected ? "is-on" : ""}`}><MessageCircle size={22} /></div>
                            <div className="cp-conn-body">
                                <div className="cp-card-head">
                                    <h3 className="cp-card-title">WhatsApp</h3>
                                    {isWhatsAppConnected
                                        ? <span className="cp-pill cp-pill-ok"><Check size={12} /> Conectado</span>
                                        : <span className="cp-pill cp-pill-warn">Sin conectar</span>}
                                </div>
                                <p className="cp-muted">
                                    {isWhatsAppConnected
                                        ? <>El bot atiende y manda recordatorios desde {connectedNumber ? <b>+{connectedNumber}</b> : "tu número"}.</>
                                        : "Es el número por el que el bot atiende a tus clientes y manda los recordatorios."}
                                </p>
                                <button type="button" className={`cp-btn ${isWhatsAppConnected ? "cp-btn-danger" : "cp-btn-primary"}`} onClick={handleWhatsAppConnection} disabled={isLoadingQr}>
                                    {isWhatsAppConnected ? <><Unplug size={16} /> Desconectar</> : <><MessageCircle size={16} /> Conectar WhatsApp</>}
                                </button>
                            </div>
                        </article>
                    )}

                    <article className="cp-card cp-conn-card" data-tour="conexiones.push">
                        <div className={`cp-conn-icon ${pushPermission === 'granted' ? "is-on" : ""}`}><Bell size={22} /></div>
                        <div className="cp-conn-body">
                            <div className="cp-card-head">
                                <h3 className="cp-card-title">Notificaciones</h3>
                                {pushPermission === 'granted' && <span className="cp-pill cp-pill-ok"><Check size={12} /> Activas</span>}
                                {pushPermission === 'denied' && <span className="cp-pill cp-pill-danger">Bloqueadas</span>}
                            </div>
                            <p className="cp-muted">
                                {pushPermission === 'granted'
                                    ? "Te avisamos de cada cita nueva, cancelación y comprobante aunque no tengas el panel abierto."
                                    : pushPermission === 'denied'
                                        ? "Las bloqueaste en el navegador. Actívalas desde la configuración del sitio para recibir avisos."
                                        : "Entérate al momento de cada cita nueva, cancelación o comprobante de pago."}
                            </p>
                            {pushPermission !== 'granted' && (
                                <button type="button" className="cp-btn cp-btn-primary" onClick={handlePush} disabled={pushBusy || pushPermission === 'denied'}>
                                    {pushBusy ? <span className="cp-spin" /> : <Bell size={16} />} Activar notificaciones
                                </button>
                            )}
                        </div>
                    </article>
                </div>

                <article className="cp-card cp-conn-google" data-tour="conexiones.google">
                    <div className="cp-card-head">
                        <h3 className="cp-card-title"><CalendarCheck size={18} /> {isCurrentUserStaff ? "Mi Google Calendar" : "Google Calendar"}</h3>
                        {!isCurrentUserStaff && staffList.length > 0 && (
                            <span className={`cp-pill ${readyStaff > 0 ? "cp-pill-ok" : "cp-pill-warn"}`}>{readyStaff} de {staffList.length} conectados</span>
                        )}
                    </div>
                    <p className="cp-muted">
                        {isCurrentUserStaff
                            ? "Tus citas se guardan en tu calendario y de ahí se revisa tu disponibilidad."
                            : "Cada profesional conecta su calendario: ahí se guardan sus citas y de ahí se revisa su disponibilidad."}
                    </p>

                    {displayedStaffList.length === 0 ? (
                        <div className="cp-empty cp-conn-empty">
                            <CalendarCheck size={32} />
                            <h3>Aún no hay profesionales</h3>
                            <p>Agrega a quien atiende y conecta su Google Calendar para empezar a recibir citas.</p>
                        </div>
                    ) : (
                        <ul className="cp-list cp-conn-staff">
                            {displayedStaffList.map(staff => {
                                const ready = staff.status === "ready";
                                const Row = isCurrentUserStaff ? "div" : "button";
                                return (
                                    <li key={staff.id}>
                                        <Row type={isCurrentUserStaff ? undefined : "button"} className="cp-row" onClick={isCurrentUserStaff ? undefined : () => setStaffForEdit(staff)}>
                                            <span className="cp-avatar">{initials(staff.name)}</span>
                                            <span className="cp-row-main">
                                                <span className="cp-row-title">{staff.name}</span>
                                                <span className="cp-row-sub">
                                                    {ready ? <><Check size={13} className="cp-conn-ok" /> Calendario conectado</> : <><Clock size={13} /> Sin calendario</>}
                                                    {staff.auth?.username && <span className="cp-pill">usuario</span>}
                                                </span>
                                            </span>
                                            <span className="cp-row-end">
                                                {!ready && (
                                                    <span role="button" tabIndex={0} className="cp-btn cp-btn-primary cp-conn-connect" onClick={(e) => { e.stopPropagation(); connectGoogle(staff.id); }} onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); connectGoogle(staff.id); } }}>
                                                        Conectar
                                                    </span>
                                                )}
                                                {!isCurrentUserStaff && <ChevronRight size={18} className="cp-chev" />}
                                            </span>
                                        </Row>
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    {!isCurrentUserStaff && (
                        <button type="button" className="cp-btn cp-conn-add" data-tour="conexiones.addStaff" onClick={() => setShowAddStaff(true)}>
                            <Plus size={16} /> Agregar profesional
                        </button>
                    )}
                </article>
            </div>

            {/* Sheet: vincular WhatsApp (QR o código) */}
            <Sheet open={showWhatsAppSheet} onClose={() => setShowWhatsAppSheet(false)} side={side} title="Conectar WhatsApp" subtitle="Se cierra solo cuando quede vinculado" className="cp-vars"
                header={
                    <div className="cp-segment cp-segment-fill" role="tablist" aria-label="Método de conexión">
                        <button type="button" role="tab" aria-selected={connectMode === 'qr'} className={`cp-segment-btn ${connectMode === 'qr' ? "is-active" : ""}`} onClick={() => switchConnectMode('qr')}><QrCode size={15} /> Escanear QR</button>
                        <button type="button" role="tab" aria-selected={connectMode === 'code'} className={`cp-segment-btn ${connectMode === 'code' ? "is-active" : ""}`} onClick={() => switchConnectMode('code')}><Hash size={15} /> Usar código</button>
                    </div>
                }
            >
                <div className="cp-conn-wa">
                    {connectMode === 'qr' ? (
                        <div className="cp-conn-qr">
                            {isLoadingQr ? (
                                <div className="cp-conn-qr-box"><span className="cp-spin" /><span className="cp-muted">Generando código…</span></div>
                            ) : qrCode ? (
                                <>
                                    <div className="cp-conn-qr-box is-ready"><img src={qrCode} alt="Código QR de WhatsApp" /></div>
                                    <p className="cp-muted"><RefreshCw size={13} /> Se actualiza solo cada pocos segundos.</p>
                                </>
                            ) : (
                                <div className="cp-conn-qr-box">
                                    <AlertTriangle size={28} />
                                    <span className="cp-muted">No se pudo generar el código QR</span>
                                    <button type="button" className="cp-btn" onClick={startQr}><RefreshCw size={15} /> Reintentar</button>
                                </div>
                            )}
                        </div>
                    ) : pairingCode ? (
                        <div className="cp-conn-code">
                            <p className="cp-eyebrow">Tu código de vinculación</p>
                            <div className="cp-conn-code-value">{pairingCode}</div>
                            <p className="cp-muted">Ingrésalo en WhatsApp. Puede tardar unos segundos en vincularse.</p>
                        </div>
                    ) : (
                        <div className="cp-form">
                            <CountryPhoneSelector
                                label="Número de WhatsApp del negocio"
                                requeired={false}
                                selectedCountry={phoneCountry}
                                onCountryChange={setPhoneCountry}
                                phoneValue={phoneInput}
                                onPhoneChange={setPhoneInput}
                                phoneError={phoneError}
                                setPhoneError={setPhoneError}
                                placeholder="Sin la lada"
                                disabled={isLoadingCode}
                            />
                            <button type="button" className="cp-btn cp-btn-primary cp-btn-block" onClick={handleRequestPairingCode} disabled={isLoadingCode}>
                                {isLoadingCode ? <><span className="cp-spin" /> Generando código…</> : <><KeyRound size={16} /> Obtener código</>}
                            </button>
                        </div>
                    )}

                    <div className="cp-conn-steps">
                        <p className="cp-eyebrow">En tu teléfono</p>
                        <ol>
                            <li>Abre WhatsApp y ve a <b>Configuración › Dispositivos vinculados</b>.</li>
                            <li>Toca <b>Vincular un dispositivo</b>.</li>
                            {connectMode === 'qr'
                                ? <li>Escanea el código de arriba.</li>
                                : <><li>Elige <b>Vincular con número de teléfono</b>.</li><li>Escribe el código de 8 caracteres.</li></>}
                        </ol>
                    </div>
                </div>
            </Sheet>

            {/* Sheet: agregar profesional */}
            <AddStaffSheet open={showAddStaff} side={side} onClose={() => setShowAddStaff(false)} onSubmit={handleAddStaff} isAdding={isAddingStaff} canCreateAccount={(clientData?.staff?.length || 0) >= 1} />

            {/* Sheet: profesional (conectar / desconectar calendario, eliminar) */}
            <Sheet open={!!staffForEdit} onClose={() => setStaffForEdit(null)} side={side} title={staffForEdit?.name || ""} subtitle="Profesional" className="cp-vars"
                footer={staffForEdit && (
                    <div className={`cp-sheet-actions ${staffForEdit.status === 'ready' ? "" : "is-single"}`}>
                        {staffForEdit.status === 'ready' && (
                            <button type="button" className="cp-btn" onClick={() => handleDisconnectGoogleCalendar(staffForEdit.id)}><Unplug size={16} /> Desconectar calendario</button>
                        )}
                        <button type="button" className="cp-btn cp-btn-danger" onClick={() => handleDeleteStaff(staffForEdit.id)}><Trash2 size={16} /> Eliminar profesional</button>
                    </div>
                )}
            >
                {staffForEdit && (
                    <div className="cp-conn-staffdetail">
                        <div className="cp-row">
                            <span className="cp-avatar">{initials(staffForEdit.name)}</span>
                            <span className="cp-row-main">
                                <span className="cp-row-title">Google Calendar</span>
                                <span className="cp-row-sub">{staffForEdit.status === 'ready' ? "Conectado: sus citas se guardan ahí." : "Sin conectar: no puede recibir citas."}</span>
                            </span>
                            {staffForEdit.status === 'ready'
                                ? <span className="cp-pill cp-pill-ok"><Check size={12} /> Conectado</span>
                                : <button type="button" className="cp-btn cp-btn-primary cp-conn-connect" onClick={() => connectGoogle(staffForEdit.id)}>Conectar</button>}
                        </div>
                        <dl className="cp-kv">
                            <dt>Usuario</dt><dd>{staffForEdit.auth?.username || <span className="cp-muted">Sin cuenta propia</span>}</dd>
                        </dl>
                    </div>
                )}
            </Sheet>
        </section>
    );
}

function AddStaffSheet({ open, side, onClose, onSubmit, isAdding, canCreateAccount }) {
    const [name, setName] = useState('');
    const [createAccount, setCreateAccount] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const reset = () => { setName(''); setCreateAccount(false); setUsername(''); setPassword(''); setConfirmPassword(''); };
    const close = () => { reset(); onClose(); };
    const submit = async () => {
        const ok = await onSubmit(name.trim(), createAccount ? { username, password, confirmPassword } : null);
        if (ok) close();
    };

    return (
        <Sheet open={open} onClose={close} side={side} title="Agregar profesional" subtitle="Según los asientos de tu plan" className="cp-vars"
            footer={
                <div className="cp-sheet-actions">
                    <button type="button" className="cp-btn" onClick={close} disabled={isAdding}>Cancelar</button>
                    <button type="button" className="cp-btn cp-btn-primary" onClick={submit} disabled={isAdding || !name.trim()}>
                        {isAdding ? <><span className="cp-spin" /> Agregando…</> : "Agregar"}
                    </button>
                </div>
            }
        >
            <div className="cp-form">
                <label className="cp-field">
                    <span>Nombre</span>
                    <input type="text" className="cp-input" placeholder="Como lo conocen tus clientes" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
                </label>
                {canCreateAccount && (
                    <div className="cp-switch">
                        <div>
                            <div className="cp-switch-label">Crear cuenta de acceso</div>
                            <div className="cp-switch-hint">Para que entre al panel y vea solo su agenda.</div>
                        </div>
                        <button type="button" role="switch" aria-checked={createAccount} className="cp-toggle" onClick={() => setCreateAccount(v => !v)} aria-label="Crear cuenta de acceso" />
                    </div>
                )}
                {createAccount && (
                    <>
                        <label className="cp-field">
                            <span>Usuario</span>
                            <input type="text" className="cp-input" placeholder="nombre de usuario" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="off" autoCapitalize="none" />
                        </label>
                        <div className="cp-grid-2">
                            <label className="cp-field">
                                <span>Contraseña</span>
                                <PasswordInput className="cp-input" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
                            </label>
                            <label className="cp-field">
                                <span>Confirmar</span>
                                <PasswordInput className="cp-input" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} autoComplete="new-password" />
                            </label>
                        </div>
                    </>
                )}
            </div>
        </Sheet>
    );
}

export default ConnectionsTab;
